import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs';
import { CombinedReadonlyDirective } from '../../../../../../../directives/combined-readonly.directive';
import { ActivityEditDialogData, ActivityExperience, ExperienceData } from '../../../../../../../models';
import { AppStore } from '../../../../../../../store/store';
import { selectTripEditData } from '../../../../../../../store/trip/trip-edit/selectors-trip-edit';
import { DurationSelectComponent } from '../../../../../../shared/duration-select/duration-select.component';
import { InputLinkComponent } from '../../../../../../shared/input-link/input-link.component';
import { InputMonetaryAmountComponent } from '../../../../../../shared/input-monetary-amount/input-monetary-amount.component';
import { InputNumberComponent } from '../../../../../../shared/input-number/input-number.component';
import { InputPhoneComponent } from '../../../../../../shared/input-phone/input-phone.component';
import { InputTimeComponent } from '../../../../../../shared/input-time/input-time.component';
import { ExperienceActivityMapper, isEntity } from '../../../../../../shared/utils';
import { calculateEndTime, DurationType } from '../../../../../../shared/utils/time-utils';
import { ValidationErrorBarComponent } from '../../../../../../shared/validation-error-bar/validation-error-bar.component';
import { atLeastOneFieldValidator } from '../../../../../../validators/at-least-one.validator';
import { ActivityEditDialogBase } from '../../activity-edit-dialog-base';

@Component({
  selector: 'app-experiences-edit-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    DragDropModule,
    TranslateModule,
    ValidationErrorBarComponent,
    InputNumberComponent,
    DurationSelectComponent, InputMonetaryAmountComponent, InputTimeComponent, InputPhoneComponent,
    InputLinkComponent,
    CombinedReadonlyDirective
  ],
  templateUrl: './experiences-edit-dialog.component.html',
  styleUrl: './experiences-edit-dialog.component.css'
})
export class ExperiencesEditDialogComponent extends ActivityEditDialogBase<ActivityExperience> implements OnDestroy {


  form!: FormGroup;

  activityExperience!: ActivityExperience;

  // get the number of people of the trip from the trip-edit-context-store
  numberOfparticipants$;
  totalParticipantsFromTripContext?: number;

  constructor(
    private experienceActivityMapper: ExperienceActivityMapper,
    private store: Store<AppStore>,
    dialogRef: MatDialogRef<ExperiencesEditDialogComponent>,
    confirmationDialog: MatDialog,
    translateService: TranslateService,
    @Inject(MAT_DIALOG_DATA) public override data: ActivityEditDialogData
  ) {
    super(dialogRef, confirmationDialog, translateService, data);
    if (isEntity(data.activityData)) {
      this.activityExperience = this.experienceActivityMapper.mapFromEntityToActivity(data.activityData as ExperienceData);
    } else {
      this.activityExperience = data.activityData as ActivityExperience;
    }


    // get the number of people from the trip-edit-context store
    this.numberOfparticipants$ = this.store.select(selectTripEditData).pipe(
      map(tripData => tripData!.partecipantsTotal)
    );
    // Subscribe to number of participants from store
    this.subscriptions.push(this.numberOfparticipants$.subscribe(numberOfParticipants => {
      this.totalParticipantsFromTripContext = numberOfParticipants;
      this.initializeEditForm();
      this.setupTimeCalculation();
    }));

  }

  private initializeEditForm() {
    this.form = new FormGroup({
      name: new FormControl({ value: this.activityExperience.name, disabled: true }),

      legalName: new FormControl({ value: this.activityExperience.legalName, disabled: true }),

      startTime: new FormControl(
        this.activityExperience?.startTime ?? null, [Validators.required]),

      endTime: new FormControl({ value: this.activityExperience.endTime, disabled: true }),

      meetingPoint: new FormControl(
        this.activityExperience?.meetingPoint ?? null),

      duration: new FormControl(
        this.activityExperience?.duration ?? null, [Validators.required]),

      pricePerPerson: new FormControl(
        this.activityExperience?.pricePerPerson ?? null),

      pricePerGroup: new FormControl(
        this.activityExperience?.pricePerGroup ?? null),

      // initialize the total partecipants with the partecipants of the trip if this data is not present
      // on the activity (is not an edit existing activity)
      totalParticipants: new FormControl(
        (this.activityExperience?.totalParticipants && this.activityExperience?.totalParticipants > 0) ?
          this.activityExperience?.totalParticipants :
          this.totalParticipantsFromTripContext, [Validators.required]),

      guide: new FormControl(
        this.activityExperience?.guide ?? null),

      phone: new FormControl(
        this.activityExperience?.phone ?? null),

      description: new FormControl(
        this.activityExperience?.description ?? null),

      disclaimer: new FormControl(
        this.activityExperience?.disclaimer ?? null),

      notes: new FormControl(
        this.activityExperience?.notes ?? null),

    },
      {
        validators: atLeastOneFieldValidator('pricePerPerson', 'pricePerGroup')
      });
  }

  /**
 * Sets up automatic end time calculation when start time or duration changes
 */
  private setupTimeCalculation(): void {
    const startTimeControl = this.form.get('startTime');
    const durationControl = this.form.get('duration');

    this.subscriptions.push(startTimeControl?.valueChanges.subscribe(() => {
      this.updateEndTime();
    })!);

    this.subscriptions.push(durationControl?.valueChanges.subscribe(() => {
      this.updateEndTime();
    })!);
  }

  /**
 * Updates the end time based on start time and duration
 */
  private updateEndTime(): void {
    const startTime = this.form.get('startTime')?.value;
    const duration = this.form.get('duration')?.value as DurationType;
    const endTime = calculateEndTime(startTime, duration);
    if (endTime) {
      this.form.get('endTime')?.setValue(endTime, { emitEvent: false });
    }
  }


  confirm() {
    if (this.form.valid) {
      this.updateActivityFromForm();
      this.confirmAndCloseDialog(this.activityExperience);
    }
  }

  cancel() {
    this.dialogRef.close();
  }

  updateActivityFromForm() {
    if (this.activityExperience && this.form) {
      this.activityExperience.duration = this.form.get('duration')?.value;
      this.activityExperience.phone = this.form.get('phone')?.value;
      this.activityExperience.meetingPoint = this.form.get('meetingPoint')?.value;
      this.activityExperience.startTime = this.form.get('startTime')?.value;
      this.activityExperience.endTime = this.form.get('endTime')?.value;
      this.activityExperience.guide = this.form.get('guide')?.value;
      this.activityExperience.pricePerGroup = this.form.get('pricePerGroup')?.value;
      this.activityExperience.pricePerPerson = this.form.get('pricePerPerson')?.value;
      this.activityExperience.description = this.form.get('description')?.value;
      this.activityExperience.disclaimer = this.form.get('disclaimer')?.value;
      this.activityExperience.notes = this.form.get('notes')?.value;
      this.activityExperience.totalParticipants = this.form.get('totalParticipants')?.value;

      this.calculateTotalPrice();
    }
  }



  /**
   * Updates the total price of the activity experience with the following formula:
   *
   * totalPrice = (pricePerPerson * numberOfPeople) + pricePerGroup
   *
   */
  // totalPrice = (prezzoAPersona * this.numberOfPeople) + prezzoAgruppo
  calculateTotalPrice() {
    if (this.activityExperience) {
      const pricePerPerson = this.activityExperience.pricePerPerson ?? 0;
      const pricePerGroup = this.activityExperience.pricePerGroup ?? 0;
      const numberOfPeople = this.form.get('totalParticipants')?.value ?? 0;
      this.activityExperience.totalPrice = (pricePerPerson * numberOfPeople) + pricePerGroup;
    }

  }

}

